import React from 'react';
import styled from 'styled-components';
import { 
  FaChalkboardTeacher, 
  FaBookReader, 
  FaChartLine, 
  FaUserGraduate, 
  FaLaptop, 
  FaFileAlt 
} from 'react-icons/fa';
import SectionHeader from '../common/SectionHeader';

const DescriptionSection = styled.section`
  background-color: ${props => props.theme.colors.lightColor};
  padding: 80px 0;
`;

const DescriptionContent = styled.div`
  margin-bottom: 40px;
  
  p {
    margin-bottom: 15px;
    color: ${props => props.theme.colors.grey[600]};
    line-height: 1.8;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const HighlightsContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 30px;
  border-radius: ${props => props.theme.borders.radius.regular};
  box-shadow: ${props => props.theme.shadows.regular};
`;

const HighlightsTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xlarge};
  margin-bottom: 30px;
  text-align: center;
  color: ${props => props.theme.colors.secondaryColor};
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: flex-start;
`;

const HighlightIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(52, 152, 219, 0.1);
  color: ${props => props.theme.colors.primaryColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-right: 15px;
  flex-shrink: 0;
`;

const HighlightContent = styled.div``;

const HighlightTitle = styled.h4`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: 5px;
  color: ${props => props.theme.colors.secondaryColor};
`;

const HighlightDescription = styled.p`
  color: ${props => props.theme.colors.grey[600]};
  line-height: 1.6;
`;

const CourseDescription = () => {
  const highlights = [
    {
      icon: <FaChalkboardTeacher />,
      title: 'Expert Coaches',
      description: 'Learn from instructors with extensive experience in TNPSC examinations and deep subject knowledge.'
    },
    {
      icon: <FaBookReader />,
      title: 'Comprehensive Materials',
      description: 'Get access to well-structured study materials covering all topics in the TNPSC syllabus.'
    },
    {
      icon: <FaChartLine />,
      title: 'Regular Assessments',
      description: 'Track your progress with regular mock tests and detailed performance analytics.'
    },
    {
      icon: <FaUserGraduate />,
      title: 'Proven Success Rate',
      description: 'Join a program with 85% success rate in helping students secure positions in TNPSC exams.'
    },
    {
      icon: <FaLaptop />,
      title: 'Online Learning Portal',
      description: 'Access study materials, recorded sessions, and practice tests through our online portal.'
    },
    {
      icon: <FaFileAlt />,
      title: 'Previous Year Papers',
      description: 'Analyze and practice with previous year question papers with detailed solutions.'
    }
  ];

  return (
    <DescriptionSection>
      <div className="container">
        <SectionHeader 
          title="Course Description" 
          subtitle="Everything you need to know about our TNPSC exam coaching" 
        />
        
        <DescriptionContent>
          <p>
            Our TNPSC Combined Technical Services Examination coaching program is designed to provide comprehensive 
            preparation for candidates aspiring for Junior Draughting Officer and Overseer positions. The program 
            covers both Paper 1 (General Studies) and Paper 2 (Technical Subjects) with a focus on efficient study 
            methods and strategic exam preparation.
          </p>
          <p>
            The coaching is conducted by experienced faculty members who have a deep understanding of the examination 
            pattern and requirements. Our structured approach ensures systematic coverage of the entire syllabus, 
            along with regular assessments to track your progress and identify areas for improvement.
          </p>
          <p>
            We offer flexible timing options with both morning and evening batches to accommodate working professionals 
            and students. Our online coaching platform provides access to live sessions, recorded lectures, study materials, 
            and practice tests, ensuring a comprehensive learning experience regardless of your location.
          </p>
        </DescriptionContent>
        
        <HighlightsContainer>
          <HighlightsTitle>Program Highlights</HighlightsTitle>
          <HighlightsGrid>
            {highlights.map((highlight, index) => (
              <HighlightItem key={index}>
                <HighlightIcon>{highlight.icon}</HighlightIcon>
                <HighlightContent>
                  <HighlightTitle>{highlight.title}</HighlightTitle>
                  <HighlightDescription>{highlight.description}</HighlightDescription>
                </HighlightContent>
              </HighlightItem>
            ))}
          </HighlightsGrid>
        </HighlightsContainer>
      </div>
    </DescriptionSection>
  );
};

export default CourseDescription;
